import { Button, Input } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { HoneypotField } from "@/app/components/HoneypotField";
import { useRecaptcha } from "@/lib/recaptcha.client";
import { RECAPTCHA_ACTIONS, RENDERED_AT_FIELD, HONEYPOT_FIELD } from "@/lib/form-guard";

const { TextArea } = Input;

// Same look as the contact page's submit button (ContactForm.tsx).
const SUBMIT_CLASSES = "rounded-sm py-3 px-10 text-center hover:bg-linear-to-t! hover:from-[#ffffff25]! hover:to-gcharcoal! text-sm lg:text-md! bg-gcharcoal! text-gyellow! uppercase! font-semibold! tracking-wide transition-all duration-200 ease-in-out hover:shadow-lg active:scale-95 active:shadow-inner disabled:opacity-50";

interface FormErrors {
    name?: string;
    email?: string;
    phone?: string;
    message?: string;
}

export default function ItemContactForm({ item, setFormVisible, messageApi }: { item: any, setFormVisible: React.Dispatch<React.SetStateAction<boolean>>, messageApi: any }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [touched, setTouched] = useState<Record<string, boolean>>({});    
    const [formData, setFormData] = useState<any>({
        subject: `Inquiry for item: [${item?.GPID || ''}] ${item?.Name || ''}`,
        name: '',
        email: '',
        phone: '',
        message: '',
        source: window?.location?.href || 'https://gandpconstruction.com/inventory'
    });
    const formRef = useRef<HTMLFormElement>(null);
    // Decoy field. Anything here at submit time means a script filled it.
    const [honeypot, setHoneypot] = useState("");
    const { execute: executeRecaptcha } = useRecaptcha();
    // Stamped on mount so it measures this visitor's fill time, not build time.
    const renderedAt = useRef<number | null>(null);

    useEffect(() => {
        renderedAt.current = Date.now();
    }, []);

    // Validation rules
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string): boolean => {
        if (!phone) return true; // Phone is optional
        const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/;
        return phoneRegex.test(phone);
    };

    const validateName = (name: string): boolean => {
        return name.trim().length >= 2;
    };

    const validateMessage = (message: string): boolean => {
        return message.trim().length >= 10;
    };

    // Get validation errors
    const errors = useMemo<FormErrors>(() => {
        const errs: FormErrors = {};

        if (!validateName(formData.name)) {
            errs.name = 'Name must be at least 2 characters';
        }

        if (!formData.email) {
            errs.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            errs.email = 'Please enter a valid email address';
        }

        if (formData.phone && !validatePhone(formData.phone)) {
            errs.phone = 'Please enter a valid phone number';
        }

        if (!formData.message) {
            errs.message = 'Message is required';
        } else if (!validateMessage(formData.message)) {
            errs.message = 'Message must be at least 10 characters';
        }

        return errs;
    }, [formData]);

    const isValid = useMemo(() => {
        return Object.keys(errors).length === 0;
    }, [errors]);

    const handleBlur = (field: string) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    // Errors show once a field is touched, like antd Form validation on the contact page.
    const fieldError = (field: keyof FormErrors) => touched[field] ? errors[field] : undefined;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Mark all fields as touched
        setTouched({ name: true, email: true, phone: true, message: true });

        if (!isValid) {
            messageApi.error('Please fix the errors in the form before submitting.');
            return;
        }

        setIsProcessing(true);

        try {
            // Minted per submission. The reveal gate that showed this form
            // only set client state, so this token is what actually proves the
            // send is human.
            const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTIONS.itemInquiry);

            const response = await fetch('/api/items/inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    recaptchaToken,
                    [HONEYPOT_FIELD]: honeypot,
                    [RENDERED_AT_FIELD]: renderedAt.current,
                })
            });

            if (response.ok) {
                messageApi.success('Your message has been sent!', 5);
                setFormData({ name: '', email: '', phone: '', message: '', subject: `Inquiry for item: [${item?.GPID || ''}] ${item?.Name || ''}`, source: window?.location?.href || 'https://gandpconstruction.com/inventory' });
                setTouched({});
                setHoneypot('');
                setFormVisible(false);
            } else {
                messageApi.error('Failed to send message. Please try again.', 5);
            }
        } catch (error) {
            messageApi.error('An error occurred. Please try again.', 5);
        }

        setIsProcessing(false);
    };

    const label = (text: string, required: boolean) => (
        <label className="block pb-2 text-sm text-gray-700 font-medium">
            {required && <span className="text-[#ff4d4f] me-1">*</span>}
            {text}
        </label>
    );

    const errorText = (field: keyof FormErrors) => {
        const err = fieldError(field);
        return err ? <p className="text-[#ff4d4f] text-sm mt-1">{err}</p> : null;
    };

    return (
        <div className="w-full h-fit bg-white rounded-lg shadow-lg p-6 md:p-8">
            <form ref={formRef} onSubmit={submit} className="space-y-6">
                <HoneypotField value={honeypot} onChange={setHoneypot} idSuffix="-inquiry" />

                <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Inquire About This Item</h3>
                    <p className="text-gray-600">Fill out the form below and we&apos;ll get back to you shortly.</p>
                </div>

                <div>
                    {label('Name', true)}
                    <Input
                        size="large"
                        required
                        placeholder="Your full name"
                        className="rounded-md"
                        status={fieldError('name') ? 'error' : undefined}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        onBlur={() => handleBlur('name')}
                    />
                    {errorText('name')}
                </div>

                <div>
                    {label('Email', true)}
                    <Input
                        size="large"
                        type="email"
                        required
                        placeholder="your.email@example.com"
                        className="rounded-md"
                        status={fieldError('email') ? 'error' : undefined}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        onBlur={() => handleBlur('email')}
                    />
                    {errorText('email')}
                </div>

                <div>
                    {label('Phone', false)}
                    <Input
                        size="large"
                        type="tel"
                        placeholder="(555) 123-4567"
                        className="rounded-md"
                        status={fieldError('phone') ? 'error' : undefined}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        onBlur={() => handleBlur('phone')}
                    />
                    {errorText('phone')}
                </div>

                <div>
                    {label('Message', true)}
                    <TextArea
                        required
                        placeholder="Tell us how we can help..."
                        className="rounded-md min-h-[120px]! resize-none!"
                        status={fieldError('message') ? 'error' : undefined}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        onBlur={() => handleBlur('message')}
                    />
                    <div className="flex justify-between items-start gap-2">
                        {errorText('message') ?? <span />}
                        <span className="text-xs text-gray-400 mt-1 shrink-0">
                            {formData.message.length}/10 min
                        </span>
                    </div>
                </div>

                <div className="flex gap-3">
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isProcessing}
                        disabled={!isValid && Object.keys(touched).length > 0}
                        className={`flex-1 ${SUBMIT_CLASSES}`}
                    >
                        {isProcessing ? 'Sending...' : 'Send Inquiry'}
                    </Button>
                    <Button
                        onClick={() => setFormVisible(false)}
                        className="rounded-sm px-6 text-sm lg:text-md! uppercase! font-semibold! tracking-wide border-gcharcoal! text-gcharcoal! hover:bg-gcharcoal/5!"
                    >
                        Cancel
                    </Button>
                </div>

                <p className="text-center text-xs text-gray-400">
                    By submitting, you agree to our privacy policy
                </p>
            </form>
        </div>
    );
}
